import {useIntl} from "react-intl";
import React, {useEffect, useMemo, useRef, useState} from "react";
import '../scss/SurveyRecipients.scss';
import {useAsyncDebounce} from 'react-table'
import {getStudentName} from "../DataFunctions";
import {ReactComponent as CloseIcon} from "../svg/close.svg";
import {ReactComponent as Minimize} from "../svg/minimize-s.svg";
import {ReactComponent as Maximize} from "../svg/maximize.svg";
import {ContactsTable, EMPTY_CONTACTS} from "./Contacts";
import {
    GetAppTranslations,
    GetGeneralTranslations,
    GetUsers
} from "../api/APISurvey";
import {GetContactMeta, GetContactsAndRoles} from "../api/APIContact";
import {useDataContext, useDataContextReadOnly} from "./SurveyContextProvider";
import {useSnackbar} from "../Content";

export default function SurveyRecipients() {
    const [dataFromUpload, setDataFromUpload] = useState([])
    const intl = useIntl()
    const { value: supportRolesData } = useDataContextReadOnly('supportRolesData')
    const { value: userMeta } = useDataContextReadOnly('userMeta')
    const { value: surveyData, setValue: setSurveyData } = useDataContext('surveyData')
    const surveyFinished = surveyData.status === "FINISHED" || surveyData.status === "ARCHIVED"

    const { value: userData, setValue: setUserData } = useDataContextReadOnly('userData')
    const getUserData = GetUsers()
    const previousUserDataRef = useRef()

    useEffect(() => {
        if (getUserData.isSuccess) {
            const newData = getUserData.data

            // Check if the data has changed since the last update
            if (newData !== previousUserDataRef.current) {
                setUserData(newData)
                previousUserDataRef.current = newData // Store the new data for future comparisons
            }
        }
    }, [getUserData])

    const { value: contactsData, setValue: setContactsData } = useDataContextReadOnly('contactsData')

    const { value: contactsAndRoles, setValue: setContactsAndRoles } = useDataContextReadOnly('contactsAndRoles')
    const getContactsAndRoles = GetContactsAndRoles()
    const previousContactsAndRolesRef = useRef()

    useEffect(() => {
        if (getContactsAndRoles.isSuccess) {
            const newData = getContactsAndRoles.data

            // Check if the data has changed since the last update
            if (newData !== previousContactsAndRolesRef.current) {
                setContactsAndRoles(newData)
                setContactsData(newData?.contacts)
                previousContactsAndRolesRef.current = newData
            }
        }
    }, [getContactsAndRoles])

    const { value: contactMeta, setValue: setContactMeta } = useDataContextReadOnly('contactMeta')
    const getContactMeta = GetContactMeta()
    const previousContactMetaRef = useRef()

    useEffect(() => {
        if (getContactMeta.isSuccess) {
            const newData = getContactMeta?.data[0]

            // Check if the data has changed since the last update
            if (newData !== previousContactMetaRef.current) {
                setContactMeta(newData)
                previousContactMetaRef.current = newData
            }
        }
    }, [getContactMeta])

    const getTranslations = GetAppTranslations(intl.locale)
    const getGeneralTranslations = GetGeneralTranslations(intl.locale)

    const { setSnackbarData } = useSnackbar()
    useEffect(() => {
        if (getContactsAndRoles.isLoading) {
            setSnackbarData({
                show: true,
                text: intl.formatMessage({
                    id: 'contacts.loading',
                    defaultMessage: 'Student data is loading. Some information may be incomplete until finished.',
                }),
                state: "info",
                autoHide: false,
            })
        }
        if (getContactsAndRoles.isSuccess) {
            setSnackbarData({ show: false, text: "", state: "", autoHide: false })
        }
    }, [getContactsAndRoles.isLoading, getContactsAndRoles.isSuccess])

    return <div className={"survey-recipients"}>
        <SurveyRecipientsContent contactsData={contactsAndRoles?.contacts || EMPTY_CONTACTS}/>
        <ContactsTable
            contacts={contactsAndRoles?.contacts || EMPTY_CONTACTS}
            contactMeta={contactMeta}
            supportRolesData={supportRolesData}
            refetchContacts={()=> {
                getContactsAndRoles.refetch()
                getContactMeta.refetch()
                getTranslations.refetch()
                getGeneralTranslations.refetch()
            }}
            refetchingContacts={getContactsAndRoles.isRefetching}
            isLoading={getContactsAndRoles.isLoading}
            surveyRecipients={true}
            surveyData={surveyData}
            setSurveyData={setSurveyData}
            userData={userData}
            userMeta={userMeta}
            refetchUsers={()=>getUserData.refetch()}
        />
    </div>
}

function SurveyRecipientsContent({contactsData}) {
    const { value: surveyData, setValue: setSurveyData } = useDataContext('surveyData')
    const surveyContacts = surveyData.contacts
    const intl = useIntl()
    const [showContacts, setShowContacts] = useState(false)
    const [showEmptyConfirm, setShowEmptyConfirm] = useState(false)
    const [showSummary, setShowSummary] = useState(localStorage.getItem("showRecipientSummary") ? JSON.parse(localStorage.getItem("showRecipientSummary")) : true)
    const limitedEditing = surveyData.status === "IN PROGRESS" || surveyData.status === "FINISHED"

    const contactsMap = useMemo(() => {
        const map = new Map()
        if (contactsData) {
            contactsData.forEach(c => map.set(c.id, c))
        }
        return map
    }, [contactsData])

    const removeStudentFromSurvey = (user) => {
        setSurveyData({
            ...surveyData,
            contacts: surveyData.contacts.filter(id => id !== user)
        })
    }

    const emptySurveyOfContacts = () => {
        setSurveyData({ ...surveyData, contacts: [] })
        setShowEmptyConfirm(false)
    }

    let studentList
    studentList = surveyContacts && !showContacts ? surveyContacts.slice(0,8) : surveyContacts

    const summaryData = useMemo(() => {
        if (!surveyContacts) return []
        const groups = []
        const degrees = []
        const locations = []

        surveyContacts.forEach(studentId => {
            const student = contactsMap.get(studentId)
            const meta = student?.meta
            if (meta?.hasOwnProperty("group")) groups.push(meta.group)
            if (meta?.hasOwnProperty("degree")) degrees.push(meta.degree)
            if (meta?.hasOwnProperty("location")) locations.push(meta.location)
        })

        return [
            { header: intl.formatMessage({id:'group', defaultMessage:'Groups'}), keys: [...new Set(groups)], allKeys: groups },
            { header: intl.formatMessage({id:'degree', defaultMessage:'Degrees'}), keys: [...new Set(degrees)], allKeys: degrees },
            { header: intl.formatMessage({id:'location', defaultMessage:'Locations'}), keys: [...new Set(locations)], allKeys: locations },
        ]
    }, [surveyContacts, contactsMap, intl])

    const RenderSummary = () => {
        return summaryData.map((obj, i) => {
            return <RenderSummaryGroup obj={obj} key={i} />
        })
    }

    function RenderSummaryGroup({obj}) {
        const [maximizeGroup, setMaximizeGroup] = useState(false)
        const keys = obj && maximizeGroup ? obj.keys : obj.keys.slice(0,5)
        const extra = obj.keys.length - 5

        return <div className={"summary-group"}>
            <p>
                {obj.header} ({obj.keys.length}):
            </p>
            <div className={"summary-objects-container"}>
                {keys.map((key, i) => {
                    let amount = 0
                    for (let i = 0; i < obj.allKeys.length; i++) {
                        if (obj.allKeys[i] === key) {
                            amount++
                        }
                    }
                    if (key) {
                        return <div className={"summary-object"} key={i}>{key} ({amount})</div>
                    }
                    return <div className={"summary-object"} key={i}>
                        {intl.formatMessage(
                            {
                                id: 'survey.table.empty',
                                defaultMessage: '(Empty)',
                            })} ({amount})</div>
                })}
                {extra > 0 && !maximizeGroup &&
                    <button className={"text"} onClick={()=>setMaximizeGroup(true)}>
                        + {extra} {intl.formatMessage(
                        {
                            id: 'survey.table.more',
                            defaultMessage: 'more',
                        })}</button>
                }
                {maximizeGroup &&
                <button className={"text"} onClick={()=>setMaximizeGroup(false)}>
                    - {intl.formatMessage(
                    {
                        id: 'minimize',
                        defaultMessage: 'Minimize',
                    })}
                </button>
                }
            </div>
        </div>
    }

    return <div className={"survey-recipients-container"}>
        <h4>
            <span>
            {intl.formatMessage(
                {
                    id: 'survey.contacts.recipients',
                    defaultMessage: 'Survey Recipients',
                })}
            {surveyContacts && !surveyContacts.length < 1 &&
            <> ({surveyContacts.length})</>
            }
            </span>
            {surveyContacts && !surveyContacts.length < 1 && !limitedEditing &&
            <span className={"empty-list"}>
                {!showEmptyConfirm ?
                <button className={"text"} onClick={()=>setShowEmptyConfirm(true)}>
                    {intl.formatMessage(
                        {
                            id: 'survey.contacts.emptyList',
                            defaultMessage: 'Empty List',
                        })}
                </button> :
                    <span>
                        {intl.formatMessage(
                            {
                                id: 'areYouSure',
                                defaultMessage: 'Are you sure?',
                            })} <button className={"text red"} onClick={()=>emptySurveyOfContacts()}>
                    {intl.formatMessage(
                        {
                            id: 'yes',
                            defaultMessage: 'Yes',
                        })}
                </button> / <button className={"text"} onClick={()=>setShowEmptyConfirm(false)}>
                    {intl.formatMessage(
                        {
                            id: 'areYouSure.no',
                            defaultMessage: 'No',
                        })}
                </button>
                    </span>
                }

            </span>
            }
        </h4>
        <div>
            {!surveyContacts || surveyContacts.length < 1 ?
                <p className={"placeholder"}>
                    {intl.formatMessage(
                        {
                            id: 'survey.contacts.noRecipients',
                            defaultMessage: 'This survey doesn\'t have any recipients yet. Start by adding from the table below.',
                        })}
                </p> :
                <>
                    <div className={"frame"}>
                        <div className={"recipients-container"}>
                            {studentList.map((obj, i) => {
                                let student = contactsMap.get(obj) || null
                                let name = getStudentName(student, intl)
                                return <div className="block round" key={i}>
                                    <span>{name}</span>
                                    {!limitedEditing &&
                                    <div className={"close-icon"} onClick={() => removeStudentFromSurvey(obj)}>
                                        <CloseIcon/>
                                    </div>
                                    }
                                </div>
                            })}
                            {surveyContacts.length > 8 &&
                            <button className={"block round show-all"} onClick={()=>setShowContacts(!showContacts)}>
                                {!showContacts ?
                                    <>
                                        <Maximize />
                                        {intl.formatMessage(
                                            {
                                                id: 'showAll',
                                                defaultMessage: 'Show All',
                                            })
                                        }</> :
                                    <>
                                        <Minimize />
                                        {intl.formatMessage(
                                            {
                                                id: 'minimize',
                                                defaultMessage: 'Minimize',
                                            })}
                                    </>
                                }
                            </button>
                            }
                        </div>
                    </div>
                    <h4>
                        <span className={!showSummary ? "hidden" : ""}>
                        {intl.formatMessage(
                            {
                                id: 'survey.contacts.summary',
                                defaultMessage: 'Summary',
                            })}
                            </span>
                        <button className={"text"} onClick={()=> {
                            localStorage.setItem("showRecipientSummary", JSON.stringify(!showSummary))
                            setShowSummary(!showSummary)
                        }}>
                            {showSummary ?
                                intl.formatMessage(
                                    {
                                        id: 'hide',
                                        defaultMessage: 'Hide',
                                    }) :
                                intl.formatMessage(
                                    {
                                        id: 'show',
                                        defaultMessage: 'Show',
                                    })}
                        </button>
                    </h4>
                    {showSummary &&
                    <RenderSummary />
                    }
                </>
            }
        </div>
    </div>
}

export function GlobalFilter({globalFilter, setGlobalFilter, students, usersSearch}) {
    const intl = useIntl()
    const [value, setValue] = useState(globalFilter)
    const onChange = useAsyncDebounce(value => {
        setGlobalFilter(value || undefined)
    }, 50)

    return <input value={value || ""} className={"search-user"} type={"text"}
                  onChange={e => {
                      setValue(e.target.value);
                      onChange(e.target.value);
                  }}
                  placeholder={usersSearch ? intl.formatMessage(
                      {
                          id: 'users.searchFromUsers',
                          defaultMessage: 'Search from {n} users',
                      }, {
                          n: students
                      }
                  ) :
                      intl.formatMessage(
                          {
                              id: 'survey.contacts.searchFromContacts',
                              defaultMessage: 'Search from {n} students',
                          }, {
                              n: students
                          }
                      )
                  }
    />
}